import { isYandleAgentHostEnabled } from '@/utils/yandleAgentHost.js'
import { getYandleAgentTenantId } from '@/utils/yandleAgentTenant.js'

const WIDGET_SCRIPT_URL = 'https://voice.yandle.io/widget.js'

let scriptPromise = null
let widgetInstance = null
let widgetHooksInstalled = false

/** Bumped on each stop and each new start so stale async work cannot drive the UI. */
let liveSessionToken = 0
/** @type {((s: VoiceSessionState) => void) | null} */
let deliverSession = null
/** @type {((line: VoiceTranscriptLine) => void) | null} */
let deliverTranscript = null

let userEnded = false
let agentSpeakingClearTimer = null

/** @typedef {{ phase: string; title: string; subtitle?: string; orgName?: string }} VoiceSessionState */
/** @typedef {{ role: 'caller' | 'agent'; text: string; timestamp?: string }} VoiceTranscriptLine */

function resolveTenantId(tenantId) {
  return getYandleAgentTenantId({ tenantId, channel: 'voice' })
}

function getYandleWidgetClass() {
  if (typeof window === 'undefined') return null
  const fromGlobalObject = window.YandleWidget ?? globalThis.YandleWidget
  if (typeof fromGlobalObject === 'function') return fromGlobalObject
  try {
    const ctor = (0, eval)('typeof YandleWidget !== "undefined" ? YandleWidget : null')
    return typeof ctor === 'function' ? ctor : null
  } catch {
    return null
  }
}

/** @param {VoiceSessionState} state */
function emitSession(state) {
  deliverSession?.(state)
}

function clearAgentSpeakingDebounce() {
  if (agentSpeakingClearTimer) {
    clearTimeout(agentSpeakingClearTimer)
    agentSpeakingClearTimer = null
  }
}

function onAgentAudioChunk() {
  if (userEnded) return
  emitSession({
    phase: 'agent_speaking',
    title: 'Agent is speaking',
    subtitle: 'Listen on your speakers or headset.',
  })
  clearTimeout(agentSpeakingClearTimer)
  agentSpeakingClearTimer = setTimeout(() => {
    agentSpeakingClearTimer = null
    if (userEnded) return
    emitSession({
      phase: 'listening',
      title: "You're live",
      subtitle: 'Microphone on — speak naturally.',
    })
  }, 550)
}

function installPlaybackHook(instance) {
  if (instance.__yandlePlaybackHooked) return
  const proto = Object.getPrototypeOf(instance)
  const original = proto._playPcm
  if (typeof original !== 'function') return
  instance._playPcm = function yandleWrappedPlayPcm(b64) {
    onAgentAudioChunk()
    return original.call(this, b64)
  }
  instance.__yandlePlaybackHooked = true
}

/** @param {Record<string, unknown>} data */
function handleWidgetStatus(data) {
  const msg = typeof data?.message === 'string' ? data.message : ''
  const orgName = typeof data?.org_name === 'string' ? data.org_name : ''

  if (msg === 'connecting') {
    clearAgentSpeakingDebounce()
    emitSession({
      phase: 'connecting',
      title: 'Connecting…',
      subtitle: 'Opening a secure line to the voice agent.',
    })
    return
  }
  if (msg === 'connected') {
    clearAgentSpeakingDebounce()
    emitSession({
      phase: 'connected',
      title: 'Connected',
      subtitle: 'Waiting for the agent to join…',
      orgName: orgName || undefined,
    })
    return
  }
  if (msg === 'ready') {
    clearAgentSpeakingDebounce()
    emitSession({
      phase: 'agent_ready',
      title: 'Agent picked up',
      subtitle: orgName ? `${orgName} is on the line.` : 'The voice agent joined the call.',
      orgName: orgName || undefined,
    })
    return
  }
  if (msg === 'listening') {
    emitSession({
      phase: 'listening',
      title: "You're live",
      subtitle: 'Microphone on — speak naturally.',
    })
    return
  }
  if (msg === 'disconnected') {
    clearAgentSpeakingDebounce()
    if (userEnded) return
    emitSession({
      phase: 'reconnecting',
      title: 'Connection interrupted',
      subtitle: 'Trying to restore the call…',
    })
    return
  }
  if (msg === 'reconnecting') {
    clearAgentSpeakingDebounce()
    if (userEnded) return
    emitSession({
      phase: 'reconnecting',
      title: 'Reconnecting…',
      subtitle: 'Restoring your session.',
    })
    return
  }
  if (msg) {
    emitSession({
      phase: 'session_update',
      title: 'Call update',
      subtitle: msg,
    })
  }
}

/** @param {Record<string, unknown>} msg */
function handleWidgetTranscript(msg) {
  if (userEnded) return
  const role = msg.role === 'agent' ? 'agent' : msg.role === 'caller' ? 'caller' : null
  const text = typeof msg.text === 'string' ? msg.text.trim() : ''
  if (!role || !text) return
  const timestamp = typeof msg.timestamp === 'string' ? msg.timestamp : undefined
  deliverTranscript?.({ role, text, timestamp })
}

function installWidgetSessionHooks() {
  if (!widgetInstance || widgetHooksInstalled) return
  widgetHooksInstalled = true
  installPlaybackHook(widgetInstance)
  widgetInstance.onStatus((data) => handleWidgetStatus(data))
  if (typeof widgetInstance.onTranscript === 'function') {
    widgetInstance.onTranscript((msg) => handleWidgetTranscript(msg))
  }
  widgetInstance.onError((err) => {
    console.error('[YandleWidget]', err)
    clearAgentSpeakingDebounce()
    emitSession({
      phase: 'error',
      title: 'Something went wrong',
      subtitle: typeof err === 'string' ? err : 'Voice call could not continue.',
    })
  })
}

function loadScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Yandle voice widget requires a browser'))
  }
  if (getYandleWidgetClass()) {
    return Promise.resolve()
  }
  if (scriptPromise) {
    return scriptPromise
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${WIDGET_SCRIPT_URL}"]`)
    if (existing) {
      if (getYandleWidgetClass()) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Failed to load Yandle widget script')), {
        once: true,
      })
      return
    }
    const el = document.createElement('script')
    el.src = WIDGET_SCRIPT_URL
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('Failed to load Yandle widget script'))
    document.head.appendChild(el)
  })
  return scriptPromise
}

/**
 * @param {{ silent?: boolean }} [options]
 */
export function stopYandleVoiceCall(options = {}) {
  const { silent = false } = options
  userEnded = true
  clearAgentSpeakingDebounce()
  widgetInstance?.stop()
  if (!silent) {
    deliverSession?.({
      phase: 'call_ended',
      title: 'Call ended',
      subtitle: 'You can start a new call anytime.',
    })
  }
  liveSessionToken += 1
  deliverSession = null
  deliverTranscript = null
}

/**
 * @param {{
 *   tenantId?: string
 *   onSessionUpdate?: (state: VoiceSessionState) => void
 *   onTranscriptUpdate?: (line: VoiceTranscriptLine) => void
 * }} [options]
 */
export async function startYandleVoiceCall(options = {}) {
  const { tenantId, onSessionUpdate, onTranscriptUpdate } = options

  if (!isYandleAgentHostEnabled()) {
    throw new Error('Yandle voice agent is only available on yandle.io')
  }

  liveSessionToken += 1
  const myToken = liveSessionToken
  deliverSession = (state) => {
    if (myToken !== liveSessionToken) return
    onSessionUpdate?.(state)
  }
  deliverTranscript = (line) => {
    if (myToken !== liveSessionToken) return
    onTranscriptUpdate?.(line)
  }

  userEnded = false
  clearAgentSpeakingDebounce()

  emitSession({
    phase: 'connecting',
    title: 'Connecting…',
    subtitle: 'Opening a secure line to the voice agent.',
  })

  await loadScript()
  if (myToken !== liveSessionToken) return

  const WidgetClass = getYandleWidgetClass()
  if (typeof WidgetClass !== 'function') {
    liveSessionToken += 1
    deliverSession = null
    deliverTranscript = null
    throw new Error('YandleWidget not available after loading script')
  }
  if (!widgetInstance) {
    widgetInstance = new WidgetClass()
    widgetInstance.init({
      tenant_id: resolveTenantId(tenantId),
      server_url: 'wss://voice.yandle.io',
    })
    installWidgetSessionHooks()
  }
  if (myToken !== liveSessionToken) return

  await widgetInstance.start()
}
